import React, { useState, useEffect } from "react";
import { hooks } from 'botframework-webchat-component';
import AbilityIcon from '../../images/Ability.svg';
import useFavorites from '../../hooks/useFavorites';

const { useSendEvent } = hooks;

const OfferDetail = ({ offer, isArabic, locked = false }) => {
  const sendEvent = useSendEvent();
  const contractAccNumber = offer.contractAccNumber || offer.ContractAccNumber || "";
  
  // Use the shared favorites hook
  const { isFavorite, toggleFavorite, updateCounter } = useFavorites(contractAccNumber);
  
  console.log('OfferDetail render - contractAccNumber:', contractAccNumber, 'updateCounter:', updateCounter);

  // Add state to track image load status
  const [imageError, setImageError] = useState(false);
  
  if (!offer) return null;

  // Parse offer details into bullet points if possible
  const detailLines = offer.OfferDetails
    ? offer.OfferDetails.split(/\n|\\n|-/).filter(line => line.trim())
    : [];
  const detailLinesAr = offer.OfferDetailsAr
    ? offer.OfferDetailsAr.split(/\n|\\n|-/).filter(line => line.trim())
    : [];
    
  // Get logo URL
  const logoUrl = offer.ThumbnailUrl || offer.LogoUrl
    ? `https://webapps.dewa.gov.ae/DEWAStoreAPI/uploadedfiles/Logo/${offer.ThumbnailUrl || offer.LogoUrl}`
    : null;

  // Handle redeem offer
  const handleRedeemOffer = () => {
    const redeemEventData = {
      offerUno: offer.OfferUno,
      language: isArabic ? 'ar' : 'en',
      offerName: offer.OfferName || offer.OfferNameAr || offer.Title || `Offer ${offer.OfferUno}`,
      contractAccNumber: contractAccNumber
    };
    
    sendEvent("disableInteractions", { reason: "redeemOffer" });
    sendEvent("redeemOffer", redeemEventData);
  };

  // Handle favorite toggle
  const handleFavoriteToggle = (offer) => {
    const offerUno = offer.OfferUno;
    const offerDataFavoriteValue = offer.is_favourite === true || offer.IsFavourite === true;
    
    console.log('=== OfferDetail handleFavoriteToggle ===');
    console.log('offerUno:', offerUno);
    console.log('offerDataFavoriteValue:', offerDataFavoriteValue);
    
    const newFavoriteState = toggleFavorite(offerUno, offerDataFavoriteValue);
    console.log('newFavoriteState:', newFavoriteState);
    
    sendEvent("disableInteractions", { reason: "favoriteToggle" });
    
    if (newFavoriteState) {
      sendEvent("addToFavorite", { offerUno, contractAccNumber });
      console.log('Sent addToFavorite event');
    } else {
      sendEvent("removeFromFavourite", { offerUno, contractAccNumber });
      console.log('Sent removeFromFavourite event');
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const hasDetails = (isArabic && detailLinesAr.length > 0) || (!isArabic && detailLines.length > 0) || offer.OfferBenefits || offer.OfferBenefitsAr;
  const hasTerms = offer.OfferTermsandConditions || offer.OfferTermsandConditionsAr;

  // Determine favorite status - PRIORITY: offer data FIRST, then localStorage overrides
  const offerUno = offer.OfferUno;
  const offerDataFavoriteValue = offer.is_favourite === true || offer.IsFavourite === true;
  const shouldShowAsFavorite = isFavorite(offerUno, offerDataFavoriteValue);

  console.log(`=== OfferDetail ${offerUno} rendering ===`);
  console.log('offerDataFavoriteValue:', offerDataFavoriteValue);
  console.log('shouldShowAsFavorite:', shouldShowAsFavorite);
  console.log('updateCounter:', updateCounter);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden relative" 
      style={{ 
        maxWidth: "400px",
        marginRight: '8px'
      }}>
      {/* POD Badge */}
      {offer.IsPODOffer === 1 && (
        <div 
          className={`absolute top-4 ${(offer.is_favourite !== undefined || offer.IsFavourite !== undefined) ? (isArabic ? 'left-12' : 'right-12') : (isArabic ? 'left-4' : 'right-4')} bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full flex items-center`}
        >
          {isArabic ? (
            <>
              <span>إضافي</span>
              <img src={AbilityIcon} alt="Ability" style={{ width: '16px', height: '16px', marginLeft: '4px' }} />
            </>
          ) : (
            <>
              <img src={AbilityIcon} alt="Ability" style={{ width: '16px', height: '16px', marginRight: '4px' }} />
              <span>Extra</span>
            </>
          )}
        </div>
      )}

      {/* Favorite Star Icon */}
      {(offer.is_favourite !== undefined || offer.IsFavourite !== undefined) && (
        <div 
          className={`absolute top-4 ${isArabic ? 'left-4' : 'right-4'} ${locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onClick={(e) => {
            e.stopPropagation();
            if (!locked) {
              console.log('Star clicked, calling handleFavoriteToggle');
              handleFavoriteToggle(offer);
            }
          }}
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill={shouldShowAsFavorite ? "#FFD700" : "none"}
            stroke={shouldShowAsFavorite ? "#FFD700" : "#6b7280"} 
            strokeWidth="1.5" 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            className="transition-colors duration-200"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
          </svg>
        </div>
      )}
      
      {/* Header section */}
      <div className="p-4">
        {/* Logo container - aligned to the left with gray background fallback */}
        <div className="flex justify-start mb-3">
          {logoUrl && !imageError ? (
            <img 
              src={logoUrl} 
              alt={offer.CompanyName || "Company"}
              style={{ height: '60px', objectFit: 'contain' }}
              onError={handleImageError}
              className="ml-0"
            />
          ) : (
            <div 
              className="bg-gray-300 flex items-center justify-center ml-0 rounded"
              style={{ height: '60px', width: '60px' }}
            >
              <span className="text-gray-500 text-xs font-medium">Logo</span>
            </div>
          )}
        </div>
        
        {/* Title and company name below the logo */}
        <div>
          <div className="text-sm text-gray-500 mb-1">
            {offer.CompanyName || offer.CompanyType || ""}
          </div>
          <h3 className="font-semibold text-lg text-gray-800 line-clamp-2">
            {isArabic ? offer.OfferNameAr : offer.OfferName || "Offer Name Not Available"}
          </h3>
        </div>
      </div>
      
      {/* Package includes section - only show if we have details */}
      {hasDetails && (
        <div className="px-4 pb-3">
          <p className="text-sm text-gray-700 mb-2">
            {isArabic ? "الباقة تشمل:" : "Package includes:"}
          </p>
          <ul className={`text-sm text-gray-600 space-y-1 ${isArabic ? 'pr-6' : 'pl-6'} list-disc`}>
            {/* Show Arabic details if available and Arabic is selected */}
            {isArabic && detailLinesAr.length > 0 && detailLinesAr.map((line, index) => (
              <li key={index}>{line.trim()}</li>
            ))}
            
            {/* Show English details if Arabic not available or English is selected */}
            {!isArabic && detailLines.length > 0 && detailLines.map((line, index) => (
              <li key={index}>{line.trim()}</li>
            ))}
            
            {/* Show benefits if available */}
            {isArabic && offer.OfferBenefitsAr && (
              <li>{offer.OfferBenefitsAr}</li>
            )}
            {!isArabic && offer.OfferBenefits && (
              <li>{offer.OfferBenefits}</li>
            )}
          </ul>
        </div>
      )}
      
      {/* Terms & Conditions - only show if available */}
      {hasTerms && (
        <div className="px-4 pb-3">
          <p className="text-sm text-gray-700 mb-1">
            {isArabic ? "الشروط والأحكام:" : "Terms & Conditions:"}
          </p>
          <p className="text-sm text-gray-600">
            {isArabic ? offer.OfferTermsandConditionsAr : offer.OfferTermsandConditions}
          </p>
        </div>
      )}
      
      {/* Redeem Button */}
      <div className="p-4 pt-2">
        <button 
          className={`w-full bg-emerald-700 text-white py-3 rounded-full ${locked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-emerald-800'} transition-colors font-medium`}
          onClick={() => !locked && handleRedeemOffer()}
          disabled={locked}
        >
          {isArabic ? "استرداد العرض" : "Redeem Offer"}
        </button>
      </div>
    </div>
  );
};

export default OfferDetail;